import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminContractors from "@/pages/AdminContractors";
import AdminAgents from "@/pages/AdminAgents";
import AdminRepairRequests from "@/pages/AdminRepairRequests";
import AdminActivity from "@/pages/AdminActivity";
import AdminContractorProfile from "@/pages/AdminContractorProfile";
import AdminAgentProfile from "@/pages/AdminAgentProfile";
import ContractorLogin from "@/pages/ContractorLogin";
import ContractorPortal from "@/pages/ContractorPortal";
import AgentLogin from "@/pages/AgentLogin";
import AgentPortal from "@/pages/AgentPortal";
import SetPassword from "@/pages/SetPassword";
import LoginHub from "@/pages/LoginHub";
import ResetPassword from "@/pages/ResetPassword";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import AdminBlog from "@/pages/AdminBlog";
import ServiceHub from "@/pages/ServiceHub";
import ServicePage from "@/pages/ServicePage";
import NotFound from "@/pages/NotFound";
import { Privacy, Terms } from "@/pages/Legal";

// Swap the PWA manifest per section so "Add to Home Screen" installs the right portal:
// /admin -> HPC Admin, /portal -> HPC Contractor, /agent -> HPC Agent, everything else -> main site.
const SECTION_MANIFESTS = [
  ["/admin", "/manifest-admin.json"],
  ["/portal", "/manifest-contractor.json"],
  ["/agent", "/manifest-agent.json"],
];

function ManifestManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (link) {
      const match = SECTION_MANIFESTS.find(([prefix]) => pathname.startsWith(prefix));
      link.href = match ? match[1] : "/manifest.json";
    }
    // GA4: report SPA route changes as page views
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", { page_path: pathname });
    }
  }, [pathname]);
  return null;
}

function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <AuthProvider>
          <ManifestManager />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/sc/:group" element={<ServiceHub />} />
            <Route path="/sc/:group/:slug" element={<ServicePage />} />
            {/* Hero SEO link slugs — alias to the existing hubs so links never 404 */}
            <Route path="/general-contractor" element={<Navigate to="/sc/major-projects/" replace />} />
            <Route path="/home-services" element={<Navigate to="/sc/home-services/" replace />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/login" element={<LoginHub />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/contractors" element={<AdminContractors />} />
            <Route path="/admin/agents" element={<AdminAgents />} />
            <Route path="/admin/repair-requests" element={<AdminRepairRequests />} />
            <Route path="/admin/activity" element={<AdminActivity />} />
            <Route path="/admin/blog" element={<AdminBlog />} />
            <Route path="/admin/contractors/:cid" element={<AdminContractorProfile />} />
            <Route path="/admin/agents/:aid" element={<AdminAgentProfile />} />
            <Route path="/portal" element={<ContractorPortal />} />
            <Route path="/portal/login" element={<ContractorLogin />} />
            <Route path="/portal/set-password" element={<SetPassword kind="contractor" />} />
            <Route path="/agent" element={<AgentPortal />} />
            <Route path="/agent/login" element={<AgentLogin />} />
            <Route path="/agent/set-password" element={<SetPassword kind="agent" />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
